import React, { useRef } from "react";
import { useEffect, useState } from "react";
import Lottie from "lottie-react";
import { useNavigate, useLocation } from "react-router-dom";
import animationData from "../indexAnim.json";
import { IndexAIDemo } from "./IndexAIDemo";
import { AutocompleteLocation } from "./AutocompleteLocation";
import { useTheme } from "../hooks/ThemeContext";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "../styles/TutorialDriver.css";
import { TourButton } from "./TourButton";
import "../styles/NewIndexPage.css";
import IndexFeatures from "./IndexFeatures";
import { IndexHeaderNav } from "./IndexHeaderNav";
import * as Sentry from '@sentry/react';

function IndexBody({ user = { na: true }, errormessage = "", totalJobSize }) {
  const [locationOn, setLocationOn] = useState(false); //Location box open or not
  const [location, setLocation] = useState(""); //Location value
  const [termsOn, setTermsOn] = useState(false); //Terms box open or not
  const [localErrorMsg, setLocalErrorMsg] = useState(""); //Local error messages from form actions
  const resume = useRef(null); //Reference to resume submit form
  const progressBarRef = useRef(null); //Reference to progress bar
  const progressBarFillRef = useRef(null); //Reference to fill of progress bar
  const transitionOverlayRef = useRef(null); //Reference to transition overlay
  const theForm = useRef(null); //Reference to main form
  const termsCheck = useRef(null); //Reference to terms checkbox
  const locationRef = useRef(null); //Reference to location box
  const [locationIsValid, setLocationIsValid] = useState(false);
  const [showReturnBtn, setShowReturnBtn] = useState(false);
  const [showResumeSupport, setShowResumeSupport] = useState(
    window.location.hostname.split(".")[0].toLowerCase() === "harpercollege"
  );

  const locationRouter = useLocation();



  const INDEX_TUTORIAL = "index_tutorial"; //localStorage key for the index tutorial status
  const driverRef = useRef(
    driver({
      showProgress: true,
      disableActiveInteraction: true,
      overlayClickBehavior: () => {},
      smoothScroll: true,
      popoverClass: "rezify-theme",
      onCloseClick: () => {
        localStorage.setItem(INDEX_TUTORIAL, true);
        driverRef.current.destroy();
      },
      steps: [
        {
          popover: {
            title: "Welcome to Rezify!",
            description: "Would you like to take a quick tour to get started?",
            disableButtons: [],
            nextBtnText: "Start Tour",
            prevBtnText: "Skip Tour",
            showProgress: false,
            onPrevClick: () => {
              driverRef.current.moveTo(4);
            },
          },
        },
        {
          element: ".big-button",
          popover: {
            title: "Upload Resume",
            description: "Upload your resume for analysis.",
            side: "right",
          },
        },
        {
          element: ".cta-right",
          popover: {
            title: "Terms and Conditions",
            description:
              "You may filter by location here and on the results page. Accept the terms and" +
              " conditions to view your results.",
            side: "right",
          },
        },
        {
          element: ".login-profile-section",
          popover: {
            title: "Login/Profile",
            description:
              "You can login here or on the results page. When logged in," +
              " you will be able to access your profile/results from this navigation bar.",
            side: "right",
          },
        },
        {
          element: ".tour-button",
          popover: {
            title: "Review Tour",
            description:
              "You may review this tour anytime by clicking this button.",
            side: "top",
            onNextClick: () => {
              localStorage.setItem(INDEX_TUTORIAL, true);
              driverRef.current.moveNext();
            },
          },
        },
      ],
    })
  );

  useEffect(() => {
    if (!localStorage.getItem(INDEX_TUTORIAL)) {
      driverRef.current.drive();
    }
  }, []);

  const { theme } = useTheme(); //get logo from theme

  const navigate = useNavigate(); //SPA (Single-page-architecture) navigator

  const showError = (msg, ms = 5000) => {
    setLocalErrorMsg(msg);
    setTimeout(() => setLocalErrorMsg(''), ms);
  };

  const HandleSubmit = (event) => {
      event.preventDefault();

      // HARD BLOCK: if they checked "Specify Location?", they must pick a valid suggestion
      if (locationOn && !locationIsValid) {
        showError("Please select a valid location from the suggestions list.");
        return;
      }

      const overlay = transitionOverlayRef.current;
      overlay.classList.add("show");

      const formdata = new FormData(theForm.current);
      formdata.append("location", location);

      setTimeout(async () => {
        try {
          const result = await fetch("/api/index", {
            method: "POST",
            body: formdata,
          });

          const data = await result.json();

          if (data.error_code === 'session_expired') {
            window.location.href = '/';
            return;
          }

          if (data.error_code === 'premium_required') {
            overlay.classList.remove("show");
            showError(data.error_message, 6000);
            setShowReturnBtn(true);
            return;
          }

          if (data.redirect) {
            navigate(data.redirect);
            return;
          }

          overlay.classList.remove("show");
          showError(data.error_message || "An error occurred. Please try again.");
        } catch (err) {
          Sentry.captureException(err);
          overlay.classList.remove("show");
          showError("Network error. Please reload the page.");
        }
      }, 1500);
    };


  const ValidateResume = (event) => {
    if (!resume.current) {
      event.preventDefault();
      return;
    }

    if (!resume.current.files.length) {
      event.preventDefault();
      showError("Upload a resume using the center button.");
      return;
    }

    if (!termsCheck.current || !termsCheck.current.checked) {
      event.preventDefault();
      showError("You must accept the Terms and Conditions to proceed.");
      return;
    }
    HandleSubmit(event);
  };

  const ValidateFile = () => {
    const fileInput = resume.current;
    const file = fileInput.files[0];

    if (file) {
      const maxSizeKB = 800;
      const maxSizeBytes = maxSizeKB * 1024; // 400 KB

      if (file.size > maxSizeBytes) {
        showError(
          `File is too large! Please upload a file smaller than ${maxSizeKB}KB.`
        );
        fileInput.value = ""; // Reset file input
      } else {
        showFileName(file.name);
      }
    }
  };

  const showFileName = () => {
    const input = resume.current;
    const file = input.files[0];
    const fileNameElement = document.getElementById("file-name");

    if (file && file.type === "application/pdf") {
      const fileName = file.name;
      fileNameElement.textContent = `Selected File: ${fileName}`;
      fileNameElement.classList.add("attached");
      const progressBar = progressBarRef.current;
      const progressBarFill = progressBarFillRef.current;
      progressBar.style.display = "block";
      progressBarFill.style.width = "0";
      progressBarFill.style.animation = "loading 1s ease-in-out forwards";
      setTimeout(() => {
        fileNameElement.classList.remove("attached");
        progressBar.style.display = "none";
      }, 2000);
    } else {
      fileNameElement.textContent = "Attach Resume (PDF)";
      fileNameElement.classList.remove("attached");
      showError("Please upload a valid PDF file.");
    }
  };

  const scrollAndHighlight = (id, offset = 120) => {
      const el = document.getElementById(id);
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      // Scroll to element minus offset
      const targetY = rect.top + scrollTop - offset;

      window.scrollTo({
        top: targetY,
        behavior: "smooth",
      });

      // reset highlight
      el.classList.remove("flash-highlight");

      setTimeout(() => {
        el.classList.add("flash-highlight");

        setTimeout(() => {
          el.classList.remove("flash-highlight");
        }, 4000);
      }, 500);
    };

    useEffect(() => {
        const params = new URLSearchParams(locationRouter.search);
        const noRedir = params.get("noredir");

        if (noRedir === "1") {
            // Delay slightly to ensure DOM is rendered
            setTimeout(() => {
                scrollAndHighlight("cta-left", 400);
                // 👆 Replace "cta-left" with whatever ID you want highlighted
            }, 300);
        }
    }, [locationRouter.search]);

  return (
    <>
      <TourButton driver={driverRef.current}></TourButton>
      <IndexHeaderNav
        user={user}
        errormessage={localErrorMsg || errormessage}
        scrollAndHighlight={scrollAndHighlight}
        showReturnBtn={showReturnBtn}
      />



      <div className="hero">
        <div className="hero-left">
          <h1 className="hero-title">
            From <span className="gradient-text">Campus</span> to{" "}
            <span className="gradient-text">Career</span>.<br />
            Your <span className="gradient-text">Internship</span> Search Starts
            Here.
          </h1>

          <div className="hero-cta">
            <div id="cta-left" className="cta-left">
              <button
                className="big-button"
                onClick={() => {
                  if (user && user.plan === "basic" && user.resume_file && user.resume_file.length >= 1) {
                    showError("Upgrade to premium to upload a new resume.", 6000);
                    setShowReturnBtn(true);
                    return;
                  }
                  if (resume.current) {
                    const fileNameElement =
                      document.getElementById("file-name");
                    if (fileNameElement) {
                      fileNameElement.classList.remove("attached");
                      fileNameElement.textContent = "Attach Resume (PDF)";
                    }
                    resume.current.click();
                  }
                }}
              >
                <img src={theme.logo} alt="Theme Logo" />
              </button>

              <div className="progress-bar" ref={progressBarRef}>
                <div className="fill" ref={progressBarFillRef}></div>
              </div>

              <div id="file-name" style={{ textAlign: "center" }}>
                Attach Resume (PDF)
                <div className="tooltip">Click big button to attach resume</div>
              </div>

              {showResumeSupport && (
                <span className="resume-support-arrow" aria-hidden="true">
                  &darr;
                </span>
              )}

              <form
                method="POST"
                encType="multipart/form-data"
                onSubmit={(event) => {
                  HandleSubmit(event);
                }}
                ref={theForm}
              >
                <input
                  type="file"
                  id="resume"
                  name="resume"
                  aria-label="Upload resume PDF"
                  accept=".pdf" 
                  className="file-inp"
                  onChange={() => {
                    ValidateFile();
                  }}
                  ref={resume}
                  required
                />
              </form>
            </div>

            <div className="cta-right">
              <button
                className="action-button"
                onClick={(event) => {
                  if (
                    user &&
                    user.plan === "basic" &&
                    user.resume_file &&
                    user.resume_file.length >= 1
                  ) {
                    event.preventDefault();
                    navigate("/results");
                  } else {
                    ValidateResume(event);
                  }
                }}
              >
                Search Internships
              </button>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  id="location-checkbox"
                  onClick={(event) => {
                    setLocationOn(event.target.checked);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.target.checked = !e.target.checked;
                      setLocationOn(e.target.checked);
                    }
                  }}
                />
                Specify Location?
              </label>
              {locationOn && (
                <div id="location-container">
                  <AutocompleteLocation
                      id="index_location"
                      ref={locationRef}
                      setLocation={setLocation}
                      setIsValid={setLocationIsValid}
                    />
                  <div id="mile-container">
                    <label htmlFor="miles">within</label>
                    <input
                      type="number"
                      id="miles"
                      name="miles"
                      defaultValue="50"
                      min="1"
                      max="2000"
                      className="milesInput"
                      onChange={(event) => {
                        //setLocation(event.target.value);
                      }}
                    />
                    <span>mi</span>
                  </div>
                </div>
              )}

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  id="terms-checkbox"
                  ref={termsCheck}
                  onChange={(event) => {}}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.target.checked = !e.target.checked;
                    }
                  }}
                />
                <span>
                  I accept the <br />
                  <a
                    onClick={() => {
                      setTermsOn(!termsOn);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setTermsOn((t) => !t);
                      }
                    }}
                    className="terms-link"
                    tabIndex={0}
                    role="button"
                    aria-label="View Terms and Conditions"
                  >
                    Terms and Conditions
                  </a>
                </span>
              </label>
              {termsOn && (
                <div id="terms-modal" className="modal">
                  <div className="modal-content">
                    <span
                      className="close-btn"
                      onClick={() => {
                        setTermsOn(!termsOn);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setTermsOn((t) => !t);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label="Close Terms and Conditions"
                    >
                      &times;
                    </span>
                    <h2>User Terms and Conditions</h2>
                    <ol className="terms-list">
                      <li>
                        <p><strong>Eligibility &amp; Acceptance</strong></p>
                        <p>
                          A “User” is anyone uploading data to Rezify, whether on their own behalf or on behalf of another individual.
                          You must be at least 13 years old to use this service. Rezify does not knowingly collect personal information
                          from children under 13. By checking the box, you agree to these Terms.
                        </p>
                      </li>

                      <li>
                        <p><strong>Your Data &amp; Privacy</strong></p>
                        <p>
                          Do not upload sensitive data (SSNs, government IDs, financial information, etc.). You represent that you have the
                          authority to upload any materials provided. You grant Rezify a limited, non-exclusive, royalty-free license to store,
                          process, and analyze your content solely to provide the service.
                        </p>
                      </li>

                      <li>
                        <p><strong>AI Recommendations</strong></p>
                        <p>
                          Rezify uses AI parsing to extract and organize user-provided information to generate recommendations and insights.
                          These are for informational purposes only. We do not guarantee jobs, interviews, or visa sponsorship. You are responsible
                          for independently reviewing and verifying all opportunities.
                        </p>
                      </li>

                      <li>
                        <p><strong>Limitation of Liability</strong></p>
                        <p>
                          To the maximum extent permitted by law, Rezify is not liable for any indirect, incidental, consequential, or special damages
                          arising from your use of the service, including reliance on any recommendations or opportunities. The service is provided on an
                          &apos;as-is&apos; and &apos;as-available&apos; basis.
                        </p>
                      </li>

                      <li>
                        <p><strong>Privacy Policy</strong></p>
                        <p>
                          Use of Rezify is also subject to our Privacy Policy.
                        </p>
                      </li>
                    </ol>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showResumeSupport && (
            <div className="resume-support-callout" role="note">
              <div className="resume-support-content">
                <button
                  type="button"
                  className="resume-support-close"
                  onClick={() => setShowResumeSupport(false)}
                  aria-label="Close resume support message"
                >
                  &times;
                </button>
                <p>
                  Need help creating or updating your resume? Contact the Job Placement Resource Center for support.
                </p>
                <p className="resume-support-contact">
                  <a href="mailto:jprc@harpercollege.edu">
                    jprc@harpercollege.edu
                  </a>
                  <span aria-hidden="true"> | </span>
                  <a href="tel:8479256400">847-925-6400</a>
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="hero-right">
          <div id="scanner-container">
            <Lottie
              animationData={animationData}
              loop={true}
              autoplay={true} // <-- this is default, but you can be explicit
              style={{ width: 300, height: 300 }}
            />
          </div>
          <div className="ai-demo-container">
            <IndexAIDemo />
          </div>
        </div>

        {theme.logo && !theme.logo.includes("rezify") && (
          <div
            style={{
              position: "absolute",
              bottom: "10px",
              left: "50%",
              transform: "translateX(-50%)",
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            <a
              href="https://rezify.ai"
              style={{
                color: "var(--primary-color)",
                textDecoration: "underline",
                fontWeight: "500",
              }}
            >
              Leave School Portal
            </a>
          </div>
        )}
      </div>

      <IndexFeatures
          theme={theme}
          transitionOverlayRef={transitionOverlayRef}
          totalJobSize={totalJobSize}
        />
    </>
  );
}

export { IndexBody };